import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import HomeworkViewer from "./HomeworkViewer";
import HomeworkResult from "./HomeworkResult";
import axios from "axios";
import StudentWhiteboardTab from "./pages/StudentWhiteboardTab";
import SchoolPostsTab from "./components/StudentDashboard/SchoolPostsTab";
import StudentResourcesTab from "./components/StudentResourcesTab";
import StudentCourses from "./components/StudentCourses";
import EducationGames from "./components/EducationGames";
import { buildApiUrl } from "./config";  // ✅ Import config
import { useAuth } from "./AuthContext";
import ChatWindow from "./ChatWindow";


const StudentDashboard = () => {
  const server = buildApiUrl("");  // ✅ Server URL for photos
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // ✅ Always start at home (index 0) — landing page is always the module grid
  const [tabIndex, setTabIndex] = useState(0);
  const [student, setStudent] = useState(null);
  const [homeworks, setHomeworks] = useState([]);
  const [listeningAssignments, setListeningAssignments] = useState([]);
  const [readingAssignments, setReadingAssignments] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [resultData, setResultData] = useState({});
  const [submissionMap, setSubmissionMap] = useState({});
  const [resources, setResources] = useState([]);
  const [quickLinks, setQuickLinks] = useState([]);
  const [ttrsLogin, setTtrsLogin] = useState(null);
  
  // ✅ Add this state for whiteboards
  const [whiteboards, setWhiteboards] = useState([]);

  // ✅ Add authorInfo state
  const [authorInfo, setAuthorInfo] = useState(null);
  const [studentForm, setStudentForm] = useState("");
  
  // ✅ Homework file submission state
  const [submitModalHw, setSubmitModalHw] = useState(null);
  const [submitFile, setSubmitFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // ✅ Chat state for Teams-like interface
  const { user } = useAuth();
  const [teachers, setTeachers] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [selectedTeacher, setSelectedTeacher] = useState(null);

  // ✅ Auto-select class group chat when student data is loaded
  useEffect(() => {
    if (student?.form && !selectedChat) {
      setSelectedChat({
        type: 'group',
        id: student.form,
        name: `Form ${student.form}`,
      });
    }
  }, [student?.form]);
  const [unreadCounts, setUnreadCounts] = useState({}); // { teacherId: count, 'group': count }
  
  // ✅ Mobile menu state for SchoolPostsTab
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // 🔔 Notification state
  const [notifUnreadCount, setNotifUnreadCount] = useState(0);
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const [notifList, setNotifList] = useState([]);
  const [isLoadingNotifs, setIsLoadingNotifs] = useState(false);

  // ✅ Fetch unread message counts
  useEffect(() => {
    const fetchUnreadCounts = async () => {
      try {
        const response = await axios.get(buildApiUrl(`/api/student-chat/unread/${student?.form}/${student?.school_id}`));
        if (response.data?.unread_counts) {
          const counts = response.data.unread_counts;
          
          // Don't show group unread if group chat is currently selected or was recently opened
          const isGroupChatOpen = selectedChat?.type === 'group';
          const groupOpenedTime = localStorage.getItem(`groupChatOpened_${student?.form}`);
          const recentlyOpened = groupOpenedTime && Date.now() - parseInt(groupOpenedTime) < 300000; // 5 minutes
          
          if (isGroupChatOpen || recentlyOpened) {
            delete counts['group'];
          }
          
          // Only update if counts actually changed
          setUnreadCounts(prev => {
            const prevStr = JSON.stringify(prev);
            const newStr = JSON.stringify(counts);
            return prevStr === newStr ? prev : counts;
          });
        }
      } catch (error) {
        // Silently handle network errors (internet disconnected, etc.)
        // The app will recover when connection is restored
      }
    };

    if (student?.form && student?.school_id) {
      fetchUnreadCounts();
      const interval = setInterval(fetchUnreadCounts, 3000); // Poll every 3 seconds
      return () => clearInterval(interval);
    }
  }, [student?.form, student?.school_id, selectedChat?.type, selectedChat?.id]);

  // 🔔 Fetch notification unread count
  useEffect(() => {
    if (!student?.school_id) return;
    const fetchNotifCount = async () => {
      try {
        const res = await axios.get(buildApiUrl(`/api/notifications/${encodeURIComponent(student.school_id)}/unread-count`));
        if (res.data.success) {
          setNotifUnreadCount(res.data.total_unread);
        }
      } catch (err) {
        // silently ignore
      }
    };
    fetchNotifCount();
    const interval = setInterval(fetchNotifCount, 30000);
    return () => clearInterval(interval);
  }, [student?.school_id]);

  // 🔔 Open notification panel
  const openNotifPanel = async () => {
    setShowNotifPanel(!showNotifPanel);
    if (!showNotifPanel && student?.school_id) {
      setIsLoadingNotifs(true);
      try {
        const res = await axios.get(buildApiUrl(`/api/notifications/${encodeURIComponent(student.school_id)}?limit=20`));
        if (res.data.success) {
          setNotifList(res.data.notifications);
        }
      } catch (err) {
        console.error('Error fetching notifications:', err);
      } finally {
        setIsLoadingNotifs(false);
      }
    }
  };

  // 🔔 Handle notification click - navigate to module and mark as read
  const handleNotifClick = async (notif) => {
    if (!notif.is_read) {
      try {
        await axios.post(buildApiUrl('/api/notifications/mark-read'), {
          notification_ids: [notif.id]
        });
        setNotifUnreadCount(prev => Math.max(0, prev - 1));
        setNotifList(prev => prev.map(n => n.id === notif.id ? { ...n, is_read: true } : n));
      } catch (err) {
        console.error('Error marking notification read:', err);
      }
    }
    setShowNotifPanel(false);
    // Navigate to the module
    const tabMap = {
      "posts": 6, "assignments": 1, "resources": 2,
      "timetable": 3, "chat": 7, "courses": 8,
      "quicklinks": 4, "whiteboard": 5,
    };
    if (notif.module_id && tabMap[notif.module_id] !== undefined) {
      setTabIndex(tabMap[notif.module_id]);
    }
  };

  // 🔔 Mark all read
  const markAllNotifsRead = async () => {
    if (!student?.school_id) return;
    try {
      await axios.post(buildApiUrl('/api/notifications/mark-read'), {
        user_id: student.school_id,
        mark_all: true
      });
      setNotifUnreadCount(0);
      setNotifList(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (err) {
      console.error('Error marking notifications read:', err);
    }
  };

  // ✅ URL management functions
  const handleTabChange = (newTabIndex) => {
    setTabIndex(newTabIndex);
    const tabMapping = ['home', 'homework', 'resources', 'timetable', 'quicklinks', 'whiteboard', 'posts'];
    const tabName = tabMapping[newTabIndex];
    if (newTabIndex === 0) {
      setSearchParams({});
    } else {
      setSearchParams({ tab: tabName });
    }
  };

  const handleBackToHome = () => {
    handleTabChange(0);
  };

  // Sync tabIndex with URL changes (browser back/forward)
  useEffect(() => {
    const urlTab = searchParams.get('tab');
    const tabMapping = {
      'home': 0,
      'homework': 1,
      'resources': 2,
      'timetable': 3,
      'schedule': 3, // Backwards compatibility
      'quicklinks': 4,
      'whiteboard': 5,
      'posts': 6
    };
    const targetTab = urlTab ? (tabMapping[urlTab] ?? 0) : 0;
    if (targetTab !== tabIndex) {
      setTabIndex(targetTab);
    }
  }, [searchParams]);

  // Back Button Component
  const BackButton = ({ className = "" }) => (
    <button
      onClick={handleBackToHome}
      className={`flex items-center gap-2 p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100/70 rounded-lg transition-all duration-200 ${className}`}
      title="Back to Dashboard"
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
      </svg>
      <span className="text-sm font-medium">Back</span>
    </button>
  );

  // ✅ PREVENT AUTO-SCROLLING in StudentDashboard
  useEffect(() => {
    // Disable auto-scroll behaviors for this component
    document.body.style.scrollBehavior = 'auto';
    document.documentElement.style.scrollBehavior = 'auto';
    
    // Prevent auto-focus from causing unwanted scrolling
    const preventScrollOnFocus = (e) => {
      e.preventDefault();
      if (e.target && e.target.focus) {
        e.target.focus({ preventScroll: true });
      }
    };
    
    document.addEventListener('focusin', preventScrollOnFocus);
    
    return () => {
      document.removeEventListener('focusin', preventScrollOnFocus);
    };
  }, []);

  useEffect(() => {
    const schoolId = localStorage.getItem("schoolId");
    if (!schoolId) return;

    axios.get(buildApiUrl("/synced-students"))
      .then(res => {
        const found = res.data.students.find(s => s.school_id === schoolId);
        setStudent(found);
        if (found?.school_id) {
          localStorage.setItem("studentId", found.school_id);
          // ✅ Set up student authentication data
          localStorage.setItem("userRole", "student");
          if (found.email) {
            localStorage.setItem("userEmail", found.email);
          } else {
            localStorage.setItem("userEmail", `${found.school_id}@student.school.com`);
          }
          if (found.full_name) {
            localStorage.setItem("userName", found.full_name);
            localStorage.setItem("studentName", found.full_name); // ✅ Add for chat
          }
          
          // Load listening assignments for student's year
          console.log('📚 Student data:', found);
          console.log('📚 Student form/year:', found.form);
          
          if (found.form) {
            // Extract year number from form (e.g., "5C" -> "5", "Pre-School" -> "Pre-School")
            let yearGroup = found.form;
            
            // If form contains a number followed by a letter (like "5C"), extract just the number
            const yearMatch = found.form.match(/^(\d+)/);
            if (yearMatch) {
              yearGroup = `Year ${yearMatch[1]}`;
            }
            
            console.log('🎯 Normalized year group:', yearGroup);
            
            const apiUrl = buildApiUrl(`/api/listening/student/${encodeURIComponent(yearGroup)}`);
            console.log('🔗 Fetching listening assignments from:', apiUrl);
            
            axios.get(apiUrl)
              .then(res => {
                console.log('🎧 Listening assignments API response:', res.data);
                console.log('🎧 Number of assignments:', res.data.assignments?.length || 0);
                if (res.data.success) {
                  setListeningAssignments(res.data.assignments || []);
                }
              })
              .catch(err => {
                console.error('❌ Error loading listening assignments:', err);
                setListeningAssignments([]);
              });

            // Load reading assignments for student's year
            const readingApiUrl = buildApiUrl(`/api/reading/student/${encodeURIComponent(yearGroup)}`);
            console.log('📖 Fetching reading assignments from:', readingApiUrl);
            
            axios.get(readingApiUrl)
              .then(res => {
                console.log('📖 Reading assignments API response:', res.data);
                if (res.data.success) {
                  setReadingAssignments(res.data.assignments || []);
                }
              })
              .catch(err => {
                console.error('❌ Error loading reading assignments:', err);
                setReadingAssignments([]);
              });
          } else {
            console.warn('⚠️ Student has no form/year field');
          }
        }
      });

    axios.get(buildApiUrl(`/student-homework/${schoolId}`))
      .then(res => setHomeworks(res.data.homework || []));

    axios.get(buildApiUrl("/homework-result-map"))
      .then(res => {
        console.log("📊 Homework submission map received:", res.data);
        console.log("   Number of students with submissions:", Object.keys(res.data || {}).length);
        setSubmissionMap(res.data || {});
      })
      .catch(err => {
        console.error("❌ Error fetching homework submission map:", err);
        setSubmissionMap({});
      });

    axios.get(buildApiUrl("/api/resources"))
      .then(res => setResources(res.data || []))
      .catch((error) => {
        console.log('ℹ️ Resources endpoint not available, skipping...');
        setResources([]);
      });

    // Fetch quick links from API
    axios.get(buildApiUrl('/api/quick-links'))
      .then((res) => {
        if (res.data.success) {
          setQuickLinks(res.data.quick_links);
        }
      })
      .catch((error) => {
        console.log('ℹ️ Quick links endpoint not available, skipping...');
        setQuickLinks([]);
      });

    fetch("/data/ttrs.json")
      .then((res) => res.json())
      .then((data) => {
        const found = data.find((s) => s.schoolId === schoolId);
        setTtrsLogin(found);
      })
      .catch(() => setTtrsLogin(null));
  }, []);

  // ✅ Add this new useEffect for fetching whiteboards
  useEffect(() => {
    const form = student?.form;
    if (!form) return;

    axios.get(buildApiUrl(`/api/whiteboard/form/${form}`))
      .then((res) => {
        setWhiteboards(res.data);
      })
      .catch((err) => {
        console.error("❌ Error loading student whiteboards", err);
        setWhiteboards([]); // Reset to empty array on error
      });
  }, [student]); // Runs when student data changes

  // ✅ Load authorInfo from localStorage on component mount
  useEffect(() => {
    const loadAuthorInfo = () => {
      try {
        const userEmail = localStorage.getItem('userEmail');
        const userRole = localStorage.getItem('userRole');
        const schoolId = localStorage.getItem('schoolId');
        const teacherId = localStorage.getItem('teacherId');
        
        console.log("📋 Loading authorInfo from localStorage:", {
          userEmail, userRole, schoolId, teacherId
        });
        
        if (userEmail && userRole) {
          const authData = {
            email: userEmail,
            role: userRole,
            schoolId: schoolId,
            teacherId: teacherId,
            // ✅ Add actual user information for better post attribution
            email: localStorage.getItem('teacherEmail') || 'hshehadeh@sherborneqatar.com',
            name: 'Hisham Shehadeh' // Set actual teacher name
          };
          
          setAuthorInfo(authData);
          console.log("✅ Loaded authorInfo:", authData);
        } else {
          console.warn("⚠️ Missing user data in localStorage, setting default teacher info");
          // Set default teacher information instead of student
          setAuthorInfo({ 
            role: 'teacher',
            email: 'hshehadeh@sherborneqatar.com',
            name: 'Hisham Shehadeh'
          });
        }
      } catch (error) {
        console.error("❌ Error loading authorInfo:", error);
        // Set default teacher information on error
        setAuthorInfo({ 
          role: 'teacher',
          email: 'hshehadeh@sherborneqatar.com',
          name: 'Hisham Shehadeh'
        });
      }
    };
    
    loadAuthorInfo();
  }, []);

  // ✅ Load teachers for Teams-like chat interface
  useEffect(() => {
    const form = student?.form;
    if (!form) return;

    axios
      .get(buildApiUrl(`/staff/by-form/${form}`))
      .then((res) => {
        const assignedTeachers = res.data.staff || [];
        setTeachers(assignedTeachers);
        console.log("✅ Loaded teachers for form", form, ":", assignedTeachers);
      })
      .catch((err) => {
        console.error("❌ Error loading teachers:", err);
        setTeachers([]);
      });
  }, [student]);

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'overdue':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'upcoming':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getResourceIcon = (type) => {
    switch (type) {
      case 'pdf': return '📄';
      case 'document': return '📝';
      case 'link': return '🔗';
      default: return '📁';
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = "/";
  };

  const tabs = [
    { id: 0, label: "Home", icon: "🏠" },
    { id: 1, label: "Assignments", icon: "📝" },
    { id: 2, label: "Resources", icon: "📚" },
    { id: 3, label: "Timetable", icon: "📅" },
    { id: 4, label: "Quick Links", icon: "🔗" },
    { id: 5, label: "WhiteBoard", icon: "🧑‍🏫" },
    { id: 6, label: "Posts", icon: "📢" }
  ];

  // For StudentDashboard.js - Enhanced TabButton with better new posts detection:
  const TabButton = ({ tab, isActive, onClick }) => {
    const studentForm = student?.form;
    
    // Enhanced new posts detection
    const checkHasNewPosts = () => {
      if (tab.id !== 6 || !studentForm) return false;
      
      const lastViewed = localStorage.getItem(`postsLastViewed_${studentForm}`);
      if (!lastViewed) return true; // No last viewed time = assume new posts
      
      // You could add more sophisticated logic here
      return false;
    };

    const hasNewPosts = checkHasNewPosts();

    return (
      <button
        onClick={() => {
          // ✅ Mark posts as read when Posts tab is clicked
          if (tab.id === 6 && studentForm) {
            localStorage.setItem(`postsLastViewed_${studentForm}`, new Date().toISOString());
            console.log(`📖 Posts marked as read for ${studentForm}`);
          }
          onClick();
        }}
        className={`
          flex items-center gap-3 px-4 py-2 rounded-lg font-medium w-full text-left
          transition-all duration-200 relative
          ${isActive 
            ? "bg-blue-600 text-white shadow-md"
            : "text-gray-700 hover:bg-blue-50 hover:text-blue-700"
          }
        `}
      >
        <span className="text-lg relative">
          {tab.icon}
          {/* ✅ Animated notification dot */}
          {hasNewPosts && (
            <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
          )}
        </span>
        <span className="flex-1">{tab.label}</span>
        {/* ✅ NEW badge with animation */}
        {hasNewPosts && (
          <span className="px-1.5 py-0.5 text-xs bg-red-500 text-white rounded-full animate-pulse">
            NEW
          </span>
        )}
      </button>
    );
  };

  // ✅ Helper function for avatar colors
  const getAvatarColor = (index) => {
    const colors = [
      'bg-gradient-to-br from-blue-500 to-blue-600',
      'bg-gradient-to-br from-green-500 to-green-600',
      'bg-gradient-to-br from-purple-500 to-purple-600',
      'bg-gradient-to-br from-pink-500 to-pink-600',
      'bg-gradient-to-br from-yellow-500 to-yellow-600',
      'bg-gradient-to-br from-red-500 to-red-600',
      'bg-gradient-to-br from-indigo-500 to-indigo-600',
      'bg-gradient-to-br from-teal-500 to-teal-600',
    ];
    return colors[index % colors.length];
  };

  // ✅ Handle new message notification from ChatWindow
  const handleNewMessage = React.useCallback(async (message) => {
    // Skip if this is just a notification that messages were marked as read
    if (message?.type === 'messages_read') {
      return;
    }
    
    // If message is for current chat, it's already shown - don't add to unread
    if (selectedChat) {
      if (selectedChat.type === 'group' && message.is_group_message) {
        // Current chat is group and message is for group - already visible
        return;
      }
      if (selectedChat.type === 'individual' && !message.is_group_message) {
        const currentTeacherId = selectedTeacher?.teacher_id || selectedTeacher?.email || selectedTeacher?.staff_id;
        if (message.sender_id === currentTeacherId || message.receiver_id === currentTeacherId) {
          // Current chat is with this teacher - already visible
          return;
        }
      }
    }
    
    // Message is not for current chat - refresh unread counts
    try {
      const response = await axios.get(buildApiUrl(`/api/student-chat/unread/${student?.form}/${student?.school_id}`));
      if (response.data?.unread_counts) {
        setUnreadCounts(response.data.unread_counts);
      }
    } catch (error) {
      console.error('Error refreshing unread counts:', error);
    }
  }, [selectedChat, selectedTeacher, student]);

  // ✅ Handle teacher selection for chat
  const handleTeacherClick = async (teacher) => {
    const teacherId = teacher.teacher_id || teacher.email || teacher.staff_id;
    
    setSelectedTeacher(teacher);
    setSelectedChat({
      type: 'individual',
      id: teacherId,
      name: teacher.name || teacher.full_name || teacher.email,
    });

    // Mark messages from this teacher as read
    try {
      await axios.post(`${server}/api/student-chat/mark-read`, {
        form_id: student?.form,
        user_id: student?.school_id,
        contact_id: teacherId,
        contact_type: 'teacher'
      });
      
      // Clear the unread count for this teacher
      setUnreadCounts(prev => {
        const updated = { ...prev };
        delete updated[teacherId];
        return updated;
      });
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  // ✅ Handle group chat selection
  const handleGroupClick = async () => {
    setSelectedTeacher(null);
    setSelectedChat({
      type: 'group',
      id: student?.form,
      name: `Form ${student?.form}`,
    });

    // Store the time when group chat was opened
    localStorage.setItem(`groupChatOpened_${student?.form}`, Date.now().toString());
    
    // Clear the unread count for group immediately
    setUnreadCounts(prev => {
      const updated = { ...prev };
      delete updated['group'];
      return updated;
    });

    // Mark group messages as read
    try {
      await axios.post(`${server}/api/student-chat/mark-read`, {
        form_id: student?.form,
        user_id: student?.school_id,
        contact_id: 'group',
        contact_type: 'group'
      });
    } catch (error) {
      console.error('Error marking group messages as read:', error);
    }
  };

  // ----- Tabs content -----
  const DashboardTab = () => {
    // Mobile view is 'chat' if a chat is selected, otherwise 'list'
    const mobileView = selectedChat ? 'chat' : 'list';
    
    // Helper function to get Mr/Ms prefix and last name
    const getTeacherDisplayName = (name) => {
      if (!name) return 'Teacher';
      
      const nameParts = name.trim().split(' ');
      const firstName = nameParts[0].toLowerCase();
      const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : nameParts[0];
      
      const commonFemaleNames = ['sarah', 'emily', 'jennifer', 'jessica', 'lisa', 'mary', 'patricia', 'linda', 'barbara', 'susan', 'karen', 'nancy', 'betty', 'margaret', 'sandra', 'ashley', 'dorothy', 'kimberly', 'donna', 'michelle', 'carol', 'amanda', 'melissa', 'deborah', 'stephanie', 'rebecca', 'laura', 'sharon', 'cynthia', 'kathleen', 'helen', 'amy', 'anna', 'angela', 'ruth', 'brenda', 'pamela', 'nicole', 'katherine', 'samantha', 'christine', 'catherine', 'virginia', 'debra', 'rachel', 'janet', 'emma', 'carolyn', 'maria', 'heather', 'diane', 'julie', 'joyce', 'evelyn', 'joan', 'victoria', 'kelly', 'christina', 'lauren', 'frances', 'martha', 'judith', 'cheryl', 'megan', 'andrea', 'ann', 'alice', 'jean', 'doris', 'jacqueline', 'kathryn', 'hannah', 'olivia', 'gloria', 'marie', 'teresa', 'sara', 'janice', 'julia', 'grace', 'judy', 'theresa', 'rose', 'beverly', 'denise', 'marilyn', 'amber', 'danielle', 'brittany', 'diana', 'abigail', 'jane', 'natalie', 'lori', 'tiffany', 'alexis', 'kayla', 'samah'];
      
      const prefix = commonFemaleNames.includes(firstName) ? 'Ms' : 'Mr';
      return `${prefix} ${lastName}`;
    };

    const handleBackToList = () => {
      setSelectedChat(null);
      setSelectedTeacher(null);
    };
    
    return (
    <div className="h-full flex gap-4">
      {/* Mobile WhatsApp-like View */}
      <div className="md:hidden w-full h-full bg-white">
        {mobileView === 'list' ? (
          /* Chat List View */
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="bg-green-600 text-white p-4 shadow-md flex items-center gap-3">
              <button onClick={() => setTabIndex(0)} className="p-1">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h2 className="text-xl font-semibold">Chats</h2>
            </div>
            
            {/* Chats List */}
            <div className="flex-1 overflow-y-auto">
              {/* Class Group Chat */}
              <div
                onClick={async () => {
                  console.log('Group chat clicked');
                  await handleGroupClick();
                  console.log('After handleGroupClick, selectedChat should be set');
                }}
                className="flex items-center gap-3 p-4 border-b border-gray-200 hover:bg-gray-50 active:bg-gray-100 cursor-pointer"
              >
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center flex-shrink-0 relative">
                  <span className="text-white font-bold text-lg">{student?.form}</span>
                  {unreadCounts['group'] > 0 && (
                    <div className="absolute -top-1 -right-1 bg-red-500 rounded-full w-5 h-5 border-2 border-white flex items-center justify-center">
                      <span className="text-white text-xs font-bold">{unreadCounts['group']}</span>
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900">Form {student?.form}</h3>
                  <p className="text-sm text-gray-500 truncate">Class Group Chat</p>
                </div>
              </div>

              {/* Teachers List */}
              {teachers.map((teacher, index) => {
                const teacherId = teacher.teacher_id || teacher.email || teacher.staff_id;
                const teacherName = teacher.name || teacher.full_name || teacher.email;
                const displayName = getTeacherDisplayName(teacherName);
                const teacherPhoto = teacher.staff_id ? `${server}/staff-photo/${teacher.staff_id}` : null;
                
                return (
                  <div
                    key={teacherId}
                    onClick={async () => {
                      console.log('Teacher chat clicked:', teacherName);
                      await handleTeacherClick(teacher);
                      console.log('After handleTeacherClick, selectedChat should be set');
                    }}
                    className="flex items-center gap-3 p-4 border-b border-gray-200 hover:bg-gray-50 active:bg-gray-100 cursor-pointer"
                  >
                    <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 relative overflow-hidden" 
                         style={{ 
                           background: !teacherPhoto ? getAvatarColor(index).replace('bg-gradient-to-br', 'linear-gradient(to bottom right,') + ')' : 'transparent'
                         }}>
                      {teacherPhoto ? (
                        <>
                          <img 
                            src={teacherPhoto} 
                            alt={teacherName} 
                            className="w-full h-full rounded-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                          <div 
                            className={`w-full h-full rounded-full flex items-center justify-center ${getAvatarColor(index)}`}
                            style={{ display: 'none' }}
                          >
                            <span className="text-white font-bold">
                              {teacherName?.split(' ').map(n => n[0]).join('').substring(0, 2)}
                            </span>
                          </div>
                        </>
                      ) : (
                        <span className="text-white font-bold">
                          {teacherName?.split(' ').map(n => n[0]).join('').substring(0, 2)}
                        </span>
                      )}
                      {unreadCounts[teacherId] > 0 && (
                        <div className="absolute -top-1 -right-1 bg-red-500 rounded-full w-5 h-5 border-2 border-white flex items-center justify-center">
                          <span className="text-white text-xs font-bold">{unreadCounts[teacherId]}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900">{displayName}</h3>
                      <p className="text-sm text-gray-500 truncate">{teacherName}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          /* Chat View */
          <div className="h-full flex flex-col">
            {selectedChat && (
              <>
                {/* Chat Header */}
                <div className="bg-green-600 text-white p-3 shadow-md flex items-center gap-3">
                  <button onClick={handleBackToList} className="p-1">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold truncate">{selectedChat.name}</h3>
                    <p className="text-xs text-green-100">{selectedChat.type === 'group' ? 'Class Group' : 'Teacher'}</p>
                  </div>
                </div>
                
                {/* Chat Window */}
                <div className="flex-1 overflow-hidden">
                  <ChatWindow
                    currentUserId={student?.school_id}
                    recipientId={selectedChat.type === 'group' ? null : (selectedTeacher?.teacher_id || selectedTeacher?.email || selectedTeacher?.staff_id)}
                    recipientName={selectedChat.name}
                    isGroup={selectedChat.type === 'group'}
                    formId={student?.form}
                    students={selectedChat.type === 'group' ? [student] : null}
                    chatType="student"
                    onNewMessage={handleNewMessage}
                  />
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Desktop View - Original Layout */}
      <div className="hidden md:flex w-full gap-4">
      {/* Left Sidebar - Class Group + Teachers */}
      <div 
        className="flex flex-col items-center gap-4 p-4 bg-white rounded-xl shadow-sm border border-gray-100"
        style={{ width: '128px', minHeight: '500px' }}
      >
        {/* Class Group Thumbnail */}
        <div className="flex flex-col items-center">
          <div
            onClick={handleGroupClick}
            className={`w-16 h-16 rounded-full flex items-center justify-center cursor-pointer transition-all duration-200 hover:scale-110 ${
              selectedChat?.type === 'group' ? 'ring-4 ring-blue-400' : ''
            } bg-gradient-to-br from-purple-500 to-purple-600 shadow-lg mb-1 relative`}
            title={`Form ${student?.form}`}
          >
            <span className="text-white font-bold text-lg">
              {student?.form}
            </span>
            {/* Unread notification indicator */}
            {unreadCounts['group'] > 0 && (
              <div className="absolute -top-1 -right-1 bg-red-500 rounded-full w-3 h-3 border-2 border-white shadow-lg" />
            )}
          </div>
          <span className="text-xs text-gray-600 font-medium text-center">Class</span>
        </div>

        {/* Divider */}
        <div className="w-16 h-px bg-gray-200"></div>

        {/* Teachers List */}
        <div className="flex flex-col items-center gap-4 overflow-y-auto" style={{ maxHeight: '400px' }}>
          {teachers.map((teacher, index) => {
            const teacherId = teacher.teacher_id || teacher.email || teacher.staff_id;
            const teacherName = teacher.name || teacher.full_name || teacher.email;
            const displayName = getTeacherDisplayName(teacherName);
            // Use staff_id to construct photo URL
            const teacherPhoto = teacher.staff_id ? `${server}/staff-photo/${teacher.staff_id}` : null;
            
            return (
              <div
                key={teacherId}
                className="flex flex-col items-center"
              >
                <div
                  onClick={() => handleTeacherClick(teacher)}
                  className={`w-16 h-16 rounded-full flex items-center justify-center cursor-pointer transition-all duration-200 hover:scale-110 ${
                    selectedChat?.type === 'individual' && selectedChat?.id === teacherId
                      ? 'ring-4 ring-blue-400'
                      : ''
                  } shadow-lg mb-1 relative overflow-hidden`}
                  title={teacherName}
                  style={{ 
                    background: !teacherPhoto ? getAvatarColor(index).replace('bg-gradient-to-br', 'linear-gradient(to bottom right,') + ')' : 'transparent'
                  }}
                >
                  {teacherPhoto ? (
                    <>
                      <img
                        src={teacherPhoto}
                        alt={teacherName}
                        className="w-full h-full rounded-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div 
                        className={`w-full h-full rounded-full flex items-center justify-center ${getAvatarColor(index)}`}
                        style={{ display: 'none' }}
                      >
                        <span className="text-white font-bold text-lg">
                          {teacherName?.split(' ').map(n => n[0]).join('').substring(0, 2)}
                        </span>
                      </div>
                    </>
                  ) : (
                    <span className="text-white font-bold text-lg">
                      {teacherName?.split(' ').map(n => n[0]).join('').substring(0, 2)}
                    </span>
                  )}
                  {/* Unread notification indicator */}
                  {unreadCounts[teacherId] > 0 && (
                    <div className="absolute -top-1 -right-1 bg-red-500 rounded-full w-3 h-3 border-2 border-white shadow-lg" />
                  )}
                </div>
                <span className="text-xs text-gray-600 font-medium text-center line-clamp-2 px-1">
                  {displayName}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Right Pane - Chat Window */}
      <div className="flex-1 bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {selectedChat ? (
          <ChatWindow
            currentUserId={student?.school_id}
            recipientId={selectedChat.type === 'group' ? null : (selectedTeacher?.teacher_id || selectedTeacher?.email || selectedTeacher?.staff_id)}
            recipientName={selectedChat.name}
            isGroup={selectedChat.type === 'group'}
            formId={student?.form}
            students={selectedChat.type === 'group' ? [student] : null}
            chatType="student"
            onNewMessage={handleNewMessage}
          />
        ) : (
          <div className="h-full flex flex-col items-center justify-center text-gray-400">
            <div className="text-6xl mb-4">💬</div>
            <p className="text-lg font-medium">Select a teacher or class group to start chatting</p>
            <p className="text-sm mt-2">Your teachers and class group are in the sidebar</p>
          </div>
        )}
      </div>
      </div>
    </div>
    );
  };

  const AssignmentsTab = () => (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
    <div className="max-w-7xl mx-auto space-y-6">
      {submitted ? (
        <HomeworkResult
          score={resultData.score}
          total={resultData.total}
          answers={resultData.answers}
          onBack={() => setSubmitted(false)}
        />
      ) : (
        <>
          {/* Header Section */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl shadow-lg p-6 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setTabIndex(0)}
                  className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center hover:bg-white/30 transition-colors"
                  title="Back to Dashboard"
                >
                  <span className="text-white text-xl">←</span>
                </button>
                <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                  <span className="text-white text-2xl">📚</span>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white mb-0.5">My Assignments</h2>
                  <p className="text-blue-100 text-sm">Track your homework and stay on top of deadlines</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-white/90 text-xs font-medium">Total Assignments</div>
                <div className="text-3xl font-bold text-white">{homeworks.length + listeningAssignments.length + readingAssignments.length}</div>
              </div>
            </div>
          </div>

          {(homeworks.length === 0 && listeningAssignments.length === 0 && readingAssignments.length === 0) ? (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
              <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-5xl">📝</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">No Assignments Yet</h3>
              <p className="text-gray-500 text-lg">Check back later for new homework assignments</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              {/* Table Header */}
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                <div className="grid grid-cols-12 gap-4 px-6 py-4 text-sm font-semibold text-gray-700">
                  <div className="col-span-4">Assignment</div>
                  <div className="col-span-2">Subject</div>
                  <div className="col-span-2">Due Date</div>
                  <div className="col-span-2">Status</div>
                  <div className="col-span-2 text-center">Action</div>
                </div>
              </div>

              {/* Table Body */}
              <div className="divide-y divide-gray-100">
                {/* Regular Homeworks */}
                {homeworks.map((hw) => {
                  const isSubmitted = submissionMap[student?.school_id]?.[hw.id];
                  const dueDate = new Date(hw.dueDate);
                  const today = new Date();
                  const daysUntilDue = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
                  const isOverdue = daysUntilDue < 0 && !isSubmitted;
                  const isDueSoon = daysUntilDue <= 3 && daysUntilDue >= 0 && !isSubmitted;
                  
                  return (
                    <div 
                      key={hw.id} 
                      className={`grid grid-cols-12 gap-4 px-6 py-5 items-center hover:bg-blue-50/50 transition-colors ${
                        isOverdue ? 'bg-red-50/30' : isDueSoon ? 'bg-yellow-50/30' : ''
                      }`}
                    >
                      {/* Assignment Title */}
                      <div className="col-span-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-lg ${
                            isSubmitted ? 'bg-green-100' : isOverdue ? 'bg-red-100' : isDueSoon ? 'bg-yellow-100' : 'bg-blue-100'
                          }`}>
                            {isSubmitted ? '✅' : isOverdue ? '⚠️' : '📝'}
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900 text-base leading-tight">{hw.title}</h3>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {Array.isArray(hw.questions) ? `${hw.questions.length} questions` : 'View details'}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Subject */}
                      <div className="col-span-2">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-purple-100 text-purple-700 text-sm font-medium">
                          <span>📚</span>
                          {hw.subject}
                        </span>
                      </div>

                      {/* Due Date */}
                      <div className="col-span-2">
                        <div className={`text-sm font-medium ${
                          isOverdue ? 'text-red-600' : isDueSoon ? 'text-yellow-600' : 'text-gray-700'
                        }`}>
                          {formatDate(hw.dueDate)}
                        </div>
                        <div className={`text-xs ${
                          isOverdue ? 'text-red-500' : isDueSoon ? 'text-yellow-500' : 'text-gray-500'
                        }`}>
                          {isOverdue 
                            ? `${Math.abs(daysUntilDue)} days overdue` 
                            : isDueSoon 
                            ? `${daysUntilDue} day${daysUntilDue !== 1 ? 's' : ''} left` 
                            : `${daysUntilDue} days remaining`
                          }
                        </div>
                      </div>

                      {/* Status */}
                      <div className="col-span-2">
                        {isSubmitted ? (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-100 text-green-700 text-sm font-medium">
                            <span>✓</span>
                            Submitted
                          </span>
                        ) : (
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium ${
                            isOverdue 
                              ? 'bg-red-100 text-red-700' 
                              : isDueSoon 
                              ? 'bg-yellow-100 text-yellow-700' 
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            <span>{isOverdue ? '⚠' : '○'}</span>
                            {isOverdue ? 'Overdue' : 'Pending'}
                          </span>
                        )}
                      </div>

                      {/* Action Button */}
                      <div className="col-span-2 text-center">
                        {!isSubmitted && (() => {
                          const hasQuestions = Array.isArray(hw.questions) && hw.questions.length > 0;
                          const hasAttachment = Array.isArray(hw.attachments) && hw.attachments.length > 0;

                          // Uploaded file homework — show Open File + Submit buttons
                          if (!hasQuestions && hasAttachment) {
                            const fileUrl = buildApiUrl(hw.attachments[0]);
                            const fileName = hw.attachments[0].split('/').pop();
                            const ext = fileName.split('.').pop().toLowerCase();
                            const icon = ext === 'pdf' ? '📄' : ['doc','docx'].includes(ext) ? '📝' : ['ppt','pptx'].includes(ext) ? '📊' : ['xls','xlsx'].includes(ext) ? '📈' : ['jpg','jpeg','png','gif'].includes(ext) ? '🖼️' : '📎';
                            return (
                              <div className="flex flex-col gap-2">
                                <a
                                  href={fileUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-xs font-medium transition-all hover:shadow-md bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
                                  title={fileName}
                                >
                                  <span>{icon}</span>
                                  Open
                                </a>
                                <button
                                  onClick={() => setSubmitModalHw(hw)}
                                  className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-xs font-medium transition-all hover:shadow-md bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700"
                                >
                                  <span>📤</span>
                                  Submit
                                </button>
                              </div>
                            );
                          }

                          // Interactive / quiz homework — Start Now button
                          return (
                            <button
                              className={`px-4 py-2 rounded-lg text-white text-sm font-medium transition-all hover:shadow-md ${
                                isOverdue 
                                  ? 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700' 
                                  : isDueSoon 
                                  ? 'bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600' 
                                  : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700'
                              }`}
                              onClick={() => {
                                if (hasQuestions) {
                                  window.location.href = `/homework-viewer/${hw.id}`;
                                } else if (hasAttachment) {
                                  window.location.href = `/interactive-homework/${hw.id}`;
                                }
                              }}
                            >
                              Start Now →
                            </button>
                          );
                        })()}
                        {isSubmitted && (
                          <span className="text-sm text-green-600 font-medium">Completed ✓</span>
                        )}
                      </div>
                    </div>
                  );
                })}
                
                {/* Listening Assignments */}
                {listeningAssignments.map((assignment) => {
                  const dueDate = assignment.dueDate ? new Date(assignment.dueDate) : null;
                  const today = new Date();
                  const daysUntilDue = dueDate ? Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24)) : null;
                  const isOverdue = daysUntilDue !== null && daysUntilDue < 0;
                  const isDueSoon = daysUntilDue !== null && daysUntilDue <= 3 && daysUntilDue >= 0;
                  
                  return (
                    <div 
                      key={`listening-${assignment.id}`} 
                      className={`grid grid-cols-12 gap-4 px-6 py-5 items-center hover:bg-pink-50/50 transition-colors ${
                        isOverdue ? 'bg-red-50/30' : isDueSoon ? 'bg-yellow-50/30' : 'bg-pink-50/10'
                      }`}
                    >
                      {/* Assignment Title */}
                      <div className="col-span-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg flex items-center justify-center text-lg bg-gradient-to-br from-pink-100 to-rose-100">
                            🎧
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900 text-base leading-tight">{assignment.title}</h3>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {assignment.questionCount} questions • Listening
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Subject */}
                      <div className="col-span-2">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-pink-100 text-pink-700 text-sm font-medium">
                          <span>🎧</span>
                          Listening
                        </span>
                      </div>

                      {/* Due Date */}
                      <div className="col-span-2">
                        {dueDate ? (
                          <>
                            <div className={`text-sm font-medium ${
                              isOverdue ? 'text-red-600' : isDueSoon ? 'text-yellow-600' : 'text-gray-700'
                            }`}>
                              {formatDate(assignment.dueDate)}
                            </div>
                            <div className={`text-xs ${
                              isOverdue ? 'text-red-500' : isDueSoon ? 'text-yellow-500' : 'text-gray-500'
                            }`}>
                              {isOverdue 
                                ? `${Math.abs(daysUntilDue)} days overdue` 
                                : isDueSoon 
                                ? `${daysUntilDue} day${daysUntilDue !== 1 ? 's' : ''} left` 
                                : `${daysUntilDue} days remaining`
                              }
                            </div>
                          </>
                        ) : (
                          <div className="text-sm text-gray-500">No due date</div>
                        )}
                      </div>

                      {/* Status */}
                      <div className="col-span-2">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                          <span>○</span>
                          Pending
                        </span>
                      </div>

                      {/* Action Button */}
                      <div className="col-span-2 text-center">
                        <button
                          className="px-4 py-2 rounded-lg text-white text-sm font-medium transition-all hover:shadow-md bg-gradient-to-r from-pink-500 to-rose-600 hover:from-pink-600 hover:to-rose-700"
                          onClick={() => {
                            navigate(`/listening-assignment/${assignment.id}`);
                          }}
                        >
                          Start Now →
                        </button>
                      </div>
                    </div>
                  );
                })}

                {/* Reading Assignments */}
                {readingAssignments.map((assignment) => {
                  return (
                    <div 
                      key={`reading-${assignment.id}`} 
                      className="grid grid-cols-12 gap-4 px-6 py-5 items-center hover:bg-amber-50/50 transition-colors bg-orange-50/10"
                    >
                      {/* Assignment Title */}
                      <div className="col-span-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg flex items-center justify-center text-lg bg-gradient-to-br from-amber-100 to-orange-100">
                            📖
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900 text-base leading-tight">{assignment.title}</h3>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Reading Assignment
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Subject */}
                      <div className="col-span-2">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-100 text-amber-700 text-sm font-medium">
                          <span>📖</span>
                          Reading
                        </span>
                      </div>

                      {/* Due Date */}
                      <div className="col-span-2">
                        <div className="text-sm text-gray-500">No due date</div>
                      </div>

                      {/* Status */}
                      <div className="col-span-2">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                          <span>○</span>
                          Pending
                        </span>
                      </div>

                      {/* Action Button */}
                      <div className="col-span-2 text-center">
                        <button
                          className="px-4 py-2 rounded-lg text-white text-sm font-medium transition-all hover:shadow-md bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700"
                          onClick={() => {
                            navigate(`/reading-assignment/${assignment.id}`);
                          }}
                        >
                          Read Now →
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
    </div>
  );

const WhiteboardsTab = () => (
  <div className="space-y-6">
    <div className="flex items-center gap-3 mb-6">
      <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center">
        <span className="text-sky-600 text-xl">🧑‍🏫</span>
      </div>
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Whiteboards</h2>
        <p className="text-gray-600">View boards shared by your teacher</p>
      </div>
    </div>
    
    {whiteboards.length === 0 ? (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <span className="text-2xl">📋</span>
        </div>
        <h3 className="text-xl font-semibold text-gray-700 mb-2">No Whiteboards Assigned</h3>
        <p className="text-gray-500">Your teacher hasn't assigned any whiteboards to your form yet.</p>
      </div>
    ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {whiteboards.map((board) => (
          <div
            key={board.id}
            className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm hover:shadow-md hover:border-sky-300 transition-all duration-200 cursor-pointer group"
          >
            <div className="mb-4">
              <h3 className="font-bold text-lg text-gray-800 mb-2 group-hover:text-sky-700 transition-colors">
                {board.title}
              </h3>
              <div className="text-sm text-gray-500 space-y-1">
                <p>📚 Form: {board.form}</p>
                <p>👨‍🏫 Teacher: {board.teacher_email || 'N/A'}</p>
                <p>📅 Created: {new Date(board.created_at).toLocaleDateString()}</p>
              </div>
            </div>
            
            <button
              onClick={() => {
                // Navigate to whiteboard viewer or open modal
                console.log("Opening whiteboard:", board);
                // You can implement navigation here
              }}
              className="w-full bg-sky-600 hover:bg-sky-700 text-white py-2 px-4 rounded-md transition-colors font-medium"
            >
              📖 Open Whiteboard
            </button>
          </div>
        ))}
      </div>
    )}
  </div>
);

  // Courses Tab Component
  const CoursesTab = () => (
    <div className="h-screen overflow-hidden">
      <StudentCourses 
        studentId={student?.school_id}
        studentForm={student?.form}
        studentYear={student?.nc_year}
      />
    </div>
  );

  const TimetableTab = () => {
    const [studentTimetables, setStudentTimetables] = useState([]);
    const [selectedTimetable, setSelectedTimetable] = useState(null);
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState('weekly'); // 'weekly' or 'daily'
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [subjectColors, setSubjectColors] = useState({});

    // Fetch subject colors from backend
    useEffect(() => {
      const fetchSubjectColors = async () => {
        try {
          const response = await fetch(buildApiUrl('/admin-subjects'));
          const data = await response.json();
          const colorMap = {};
          if (data && Array.isArray(data)) {
            data.forEach(subject => {
              colorMap[subject.name || subject.subject] = subject.color || '#6B7280';
            });
          }
          setSubjectColors(colorMap);
        } catch (error) {
          console.error('Error fetching subject colors:', error);
        }
      };
      fetchSubjectColors();
    }, []);

    // ✅ Subject color mapping - uses colors from backend (School Manager → Timetable)
    const getSubjectColor = (subjectName) => {
      if (!subjectName) return '#E5E7EB'; // Default gray for no subject
      
      // Use color from backend, fallback to default gray
      return subjectColors[subjectName] || '#6B7280';
    };

    // ✅ Get period display color (prioritize subject color for lessons, keep custom color for breaks)
    const getPeriodDisplayColor = (period, assignment) => {
      if (!period) return '#E5E7EB';
      
      // For breaks, lunch, assembly, etc., use the period's custom color
      if (period.type !== 'lesson') {
        return period.color || '#F59E0B'; // Default amber for breaks
      }
      
      // For lessons, use subject color if available, otherwise fallback to period color
      if (assignment?.subject) {
        return getSubjectColor(assignment.subject);
      }
      
      return period.color || '#3B82F6'; // Default blue for lessons without subject
    };

    // Helper function to get current day or Sunday for weekend
    const getCurrentDisplayDate = () => {
      const today = new Date();
      const dayOfWeek = today.getDay(); // 0 = Sunday, 6 = Saturday
      
      // If Friday (5) or Saturday (6), show Sunday (0)
      if (dayOfWeek === 5 || dayOfWeek === 6) {
        const sunday = new Date(today);
        sunday.setDate(today.getDate() + (7 - dayOfWeek));
        return sunday;
      }
      return today;
    };

    // Helper function to get day name from date
    const getDayName = (date) => {
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      return days[date.getDay()];
    };

    // Helper function to format date for display
    const formatDate = (date) => {
      return date.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    };

    // Helper function to navigate days
    const navigateDay = (direction) => {
      const newDate = new Date(selectedDate);
      newDate.setDate(selectedDate.getDate() + direction);
      setSelectedDate(newDate);
    };

    useEffect(() => {
      // Initialize with current display date
      setSelectedDate(getCurrentDisplayDate());
    }, []);

    useEffect(() => {
      const fetchStudentTimetables = async () => {
        if (!student?.school_id) {
          setLoading(false);
          return;
        }

        try {
          setLoading(true);
          console.log('🔍 Fetching timetables for student:', student.school_id);
          
          // Fetch timetables assigned to this student
          const response = await fetch(buildApiUrl(`/student-timetables/${student.school_id}`));
          const data = await response.json();
          
          console.log('📅 Student timetables response:', data);
          setStudentTimetables(data.timetables || []);
          
          // Auto-select the first timetable (students should only have one)
          if (data.timetables && data.timetables.length > 0) {
            setSelectedTimetable(data.timetables[0]);
          }
        } catch (error) {
          console.error('❌ Error fetching student timetables:', error);
          setStudentTimetables([]);
        } finally {
          setLoading(false);
        }
      };

      fetchStudentTimetables();
    }, [student?.school_id]);

    if (loading) {
      return (
        <div className="space-y-6">
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
              <span className="text-2xl">📅</span>
            </div>
            <p className="text-gray-500 text-lg">Loading timetables...</p>
          </div>
        </div>
      );
    }

    if (studentTimetables.length === 0) {
      return (
        <div className="space-y-6">
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">📅</span>
            </div>
            <p className="text-gray-500 text-lg">No timetables assigned yet</p>
            <p className="text-gray-400 text-sm mt-2">
              Contact your teacher if you believe this is an error
            </p>
            <div className="mt-4 p-3 bg-blue-50 rounded-lg inline-block">
              <p className="text-blue-700 text-sm">
                👤 Student ID: <strong>{student?.school_id}</strong><br/>
                📚 Form: <strong>{student?.form}</strong>
              </p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* View Mode Toggle */}
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">View:</span>
              <div className="flex bg-gray-100 p-1 rounded-lg">
                <button
                  onClick={() => setViewMode('weekly')}
                  className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                    viewMode === 'weekly' 
                      ? 'bg-blue-500 text-white' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  📅 Weekly
                </button>
                <button
                  onClick={() => setViewMode('daily')}
                  className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                    viewMode === 'daily' 
                      ? 'bg-blue-500 text-white' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  📋 Daily
                </button>
              </div>
            </div>

            {/* Daily View Date Navigation */}
            {viewMode === 'daily' && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigateDay(-1)}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  ←
                </button>
                <span className="text-sm font-medium text-gray-900 min-w-max">
                  {formatDate(selectedDate)}
                </span>
                <button
                  onClick={() => navigateDay(1)}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  →
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Selected Timetable Display */}
        {selectedTimetable && (
          <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
            <div className="p-6">
              {/* Weekly View */}
              {viewMode === 'weekly' ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
                    {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday'].map(day => (
                      <div key={day} className="bg-gray-50 rounded-lg p-3">
                        <h4 className="font-semibold text-gray-900 mb-2 text-center text-sm">{day}</h4>
                        <div className="space-y-1">
                          {selectedTimetable.days?.[day]?.map((assignment, index) => (
                            <div 
                              key={index}
                              className="p-2 rounded-md text-white text-xs"
                              style={{ backgroundColor: getPeriodDisplayColor(assignment.period, assignment) }}
                            >
                              <div className="font-medium">{assignment.subject || assignment.period?.name}</div>
                              <div className="text-xs opacity-90">
                                {assignment.period?.startTime} - {assignment.period?.endTime}
                              </div>
                              {assignment.teacher && (
                                <div className="text-xs opacity-90">
                                  👨‍🏫 {assignment.teacher}
                                </div>
                              )}
                              {assignment.room && (
                                <div className="text-xs opacity-90">
                                  🏫 {assignment.room}
                                </div>
                              )}
                            </div>
                          ))}
                          {(!selectedTimetable.days?.[day] || selectedTimetable.days[day].length === 0) && (
                            <div className="text-center text-gray-400 py-6 text-xs">
                              No periods
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Weekly Stats */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-blue-600">
                        {Object.values(selectedTimetable.days || {}).flat().filter(a => a.period?.type === 'lesson').length}
                      </div>
                      <div className="text-sm text-blue-700">Total Lessons</div>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">
                        {(() => {
                          const subjects = new Set();
                          Object.values(selectedTimetable.days || {}).flat().forEach(a => {
                            if (a.subject) subjects.add(a.subject);
                          });
                          return subjects.size;
                        })()}
                      </div>
                      <div className="text-sm text-green-700">Different Subjects</div>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-purple-600">
                        {(() => {
                          const teachers = new Set();
                          Object.values(selectedTimetable.days || {}).flat().forEach(a => {
                            if (a.teacher) teachers.add(a.teacher);
                          });
                          return teachers.size;
                        })()}
                      </div>
                      <div className="text-sm text-purple-700">Different Teachers</div>
                    </div>
                    <div className="bg-yellow-50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-yellow-600">
                        {Object.values(selectedTimetable.days || {}).flat().filter(a => a.period?.type === 'break').length}
                      </div>
                      <div className="text-sm text-yellow-700">Break Periods</div>
                    </div>
                  </div>
                </div>
              ) : (
                /* Daily View */
                <div className="space-y-6">
                  {/* Current Day Display */}
                  {(() => {
                    const dayName = getDayName(selectedDate);
                    const daySchedule = selectedTimetable.days?.[dayName] || [];
                    
                    return (
                      <div>
                        <div className="mb-4">
                          <h4 className="text-lg font-semibold text-gray-900">
                            Schedule for {dayName}
                          </h4>
                          <p className="text-sm text-gray-600">
                            {selectedDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                          </p>
                        </div>

                        {daySchedule.length === 0 ? (
                          <div className="text-center py-12">
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                              <span className="text-2xl">🏖️</span>
                            </div>
                            <p className="text-gray-500 text-lg">No classes scheduled</p>
                            <p className="text-gray-400 text-sm mt-2">
                              Enjoy your free day!
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {daySchedule.map((assignment, index) => (
                              <div 
                                key={index}
                                className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-2">
                                      <div 
                                        className="w-4 h-4 rounded-full"
                                        style={{ backgroundColor: getPeriodDisplayColor(assignment.period, assignment) }}
                                      ></div>
                                      <h5 className="font-semibold text-gray-900">
                                        {assignment.subject || assignment.period?.name}
                                      </h5>
                                      <span className="text-sm text-gray-500 font-mono">
                                        {assignment.period?.startTime} - {assignment.period?.endTime}
                                      </span>
                                    </div>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                                      {assignment.subject && (
                                        <div className="flex items-center gap-2">
                                          <span className="text-gray-500">📚</span>
                                          <span className="font-medium">{assignment.subject}</span>
                                        </div>
                                      )}
                                      {assignment.teacher && (
                                        <div className="flex items-center gap-2">
                                          <span className="text-gray-500">👨‍🏫</span>
                                          <span>{assignment.teacher}</span>
                                        </div>
                                      )}
                                      {assignment.room && (
                                        <div className="flex items-center gap-2">
                                          <span className="text-gray-500">🏫</span>
                                          <span>{assignment.room}</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Daily Stats */}
                        {daySchedule.length > 0 && (
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                            <div className="bg-blue-50 p-4 rounded-lg">
                              <div className="text-2xl font-bold text-blue-600">
                                {daySchedule.filter(a => a.period?.type === 'lesson').length}
                              </div>
                              <div className="text-sm text-blue-700">Lessons Today</div>
                            </div>
                            <div className="bg-yellow-50 p-4 rounded-lg">
                              <div className="text-2xl font-bold text-yellow-600">
                                {daySchedule.filter(a => a.period?.type === 'break').length}
                              </div>
                              <div className="text-sm text-yellow-700">Break Periods</div>
                            </div>
                            <div className="bg-green-50 p-4 rounded-lg">
                              <div className="text-2xl font-bold text-green-600">
                                {new Set(daySchedule.map(a => a.subject).filter(Boolean)).size}
                              </div>
                              <div className="text-sm text-green-700">Different Subjects</div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Debug Info */}
        <div className="mt-4 p-3 bg-gray-50 rounded-lg text-xs text-gray-600">
          <p><strong>Debug Info:</strong></p>
          <p>Student ID: {student?.school_id} | Form: {student?.form}</p>
          <p>Timetables Found: {studentTimetables.length}</p>
          <p>View Mode: {viewMode} | Selected Date: {selectedDate.toLocaleDateString()}</p>
          {studentTimetables.length > 0 && (
            <p>Available Timetables: {studentTimetables.map(t => t.name).join(', ')}</p>
          )}
        </div>
      </div>
      </div>
    );
  };

  // 2. Add QuickLinksTab component
  const QuickLinksTab = () => {
    const [quickLinkCredentials, setQuickLinkCredentials] = useState({});
    const [expandedLink, setExpandedLink] = useState(null);

    // Load credentials for quick links that have them
    useEffect(() => {
      const schoolId = student?.school_id;
      if (!schoolId) return;

      quickLinks.forEach((link) => {
        if (link.has_credentials && link.credentials_file) {
          fetch(`/data/${link.credentials_file}`)
            .then((res) => res.json())
            .then((data) => {
              const found = data.find((s) => s.schoolId === schoolId);
              if (found) {
                setQuickLinkCredentials(prev => ({
                  ...prev,
                  [link.id]: found
                }));
              }
            })
            .catch((err) => console.log(`Could not load credentials for ${link.name}`));
        }
      });
    }, [quickLinks, student]);

    const handleCardClick = (link) => {
      // If link has credentials, expand to show them
      if (link.has_credentials && quickLinkCredentials[link.id]) {
        setExpandedLink(expandedLink === link.id ? null : link.id);
      } else {
        // Otherwise, open the link directly
        window.open(link.url, '_blank');
      }
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 px-4 py-4 shadow-lg">
          <div className="max-w-7xl mx-auto flex items-center gap-3">
            <button
              onClick={() => handleTabChange(0)}
              className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-colors"
              title="Back to Home"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <span className="text-white text-lg">🔗</span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Quick Links</h2>
              <p className="text-sm text-indigo-200">Access your school platforms and tools</p>
            </div>
          </div>
        </div>

        {/* Cards */}
        <div className="max-w-7xl mx-auto px-4 pt-8 pb-6">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {quickLinks.map((link) => {
            const credentials = quickLinkCredentials[link.id];
            const isExpanded = expandedLink === link.id;

            return (
              <div
                key={link.id}
                className="bg-white border border-gray-100 rounded-xl overflow-hidden shadow-sm hover:shadow-md hover:border-indigo-300 transition cursor-pointer"
                onClick={() => handleCardClick(link)}
              >
                {link.image && (
                  <div className="relative w-full h-32 bg-gray-100">
                    <img
                      src={buildApiUrl(link.image)}
                      alt={link.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <div className="p-4 text-center">
                  <h3 className="text-lg font-semibold text-indigo-700">{link.name}</h3>
                  {link.description && (
                    <p className="text-sm text-gray-500 mt-1">{link.description}</p>
                  )}

                  {link.has_credentials && credentials && isExpanded ? (
                    <div className="mt-4 p-3 bg-indigo-50 rounded-lg text-sm text-gray-700 space-y-1">
                      {credentials.ttUsername && (
                        <p><strong>Username:</strong> {credentials.ttUsername}</p>
                      )}
                      {credentials.username && (
                        <p><strong>Username:</strong> {credentials.username}</p>
                      )}
                      {credentials.ttPassword && (
                        <p><strong>Password:</strong> {credentials.ttPassword}</p>
                      )}
                      {credentials.password && (
                        <p><strong>Password:</strong> {credentials.password}</p>
                      )}
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block mt-2 px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 text-sm w-full"
                        onClick={(e) => e.stopPropagation()}
                      >
                        🚀 Launch {link.name}
                      </a>
                    </div>
                  ) : link.has_credentials && credentials ? (
                    <div className="mt-4">
                      <p className="text-xs text-green-600 mb-2">✓ Login credentials available</p>
                      <button className="text-sm text-indigo-600 hover:text-indigo-800 font-medium">
                        Click to view credentials
                      </button>
                    </div>
                  ) : link.has_credentials ? (
                    <p className="mt-4 text-sm text-yellow-600">No login found for your account</p>
                  ) : (
                    <div className="mt-4">
                      <p className="text-xs text-gray-500 mb-2">Click to open link</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        </div>
      </div>
    );
  };

  // Home Page with Modules
  const HomePage = () => {
    const [showHamMenu, setShowHamMenu] = useState(false);

    const firstName = student?.full_name?.split(' ')[0] || 'Student';

    const totalUnreadChat = Object.values(unreadCounts).reduce((sum, c) => sum + c, 0);

    // Upcoming: not yet submitted, due date in the future, sorted soonest first
    const upcomingHomeworks = homeworks
      .filter(hw => {
        const submitted = submissionMap[student?.school_id]?.[hw.id];
        const due = new Date(hw.dueDate);
        return !submitted && due >= new Date(new Date().setHours(0, 0, 0, 0));
      })
      .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
      .slice(0, 3);

    const handleModuleClick = (moduleId) => {
      const tabMap = {
        "chat": 7,
        "courses": 8,
        "assignments": 1,
        "resources": 2,
        "timetable": 3,
        "quicklinks": 4,
        "whiteboard": 5,
        "posts": 6,
      };
      setTabIndex(tabMap[moduleId]);
    };

    const allModules = [
      { id: "assignments", label: "Assignments", icon: "📝", badge: 0 },
      { id: "courses", label: "Courses", icon: "🎓", badge: 0 },
      { id: "timetable", label: "Timetable", icon: "📅", badge: 0 },
      { id: "resources", label: "Resources", icon: "📚", badge: 0 },
      { id: "chat", label: "Messages", icon: "💬", badge: totalUnreadChat },
      { id: "posts", label: "Posts", icon: "📢", badge: 0 },
      { id: "whiteboard", label: "Whiteboard", icon: "🧑‍🏫", badge: 0 },
      { id: "quicklinks", label: "Quick Links", icon: "🔗", badge: 0 },
    ];

    return (
      <div className="min-h-screen bg-gray-100 pb-24 md:pb-8 md:max-w-md md:mx-auto md:shadow-2xl">

        {/* ── Dark Navy Hero ── */}
        <div className="relative bg-gradient-to-br from-[#0f2752] via-[#1a3a72] to-[#0d2444] overflow-hidden pb-12 pt-2 px-5">
          {/* Decorative blobs */}
          <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full bg-white/5 pointer-events-none" />
          <div className="absolute top-20 -right-6 w-28 h-28 rounded-full bg-white/5 pointer-events-none" />

          {/* Top bar */}
          <div className="relative z-10 flex items-center justify-between pt-4 mb-5">
            {/* Profile photo + name */}
            <div className="flex items-center gap-3">
              <div className="relative">
                <img
                  src={buildApiUrl(`student-photo/${student?.school_id}`)}
                  alt={student?.full_name || 'Student'}
                  className="w-16 h-16 rounded-full object-cover border-2 border-white/40 shadow-lg"
                  onError={(e) => { e.target.onerror = null; e.target.src = '/student-hero.svg'; e.target.style.objectFit = 'contain'; e.target.style.background = '#ede9fe'; }}
                />
                <span className="absolute bottom-0.5 right-0.5 w-3.5 h-3.5 bg-green-400 rounded-full border-2 border-[#0f2752]" />
              </div>
              <div>
                <p className="text-white font-bold text-sm leading-tight">{student?.full_name || 'Student'}</p>
                <p className="text-blue-300 text-xs">
                  {student?.form ? `Form ${student.form}` : 'Student'}
                  {student?.nc_year ? ` · Year ${student.nc_year}` : ''}
                </p>
              </div>
            </div>

            {/* Bell + Settings */}
            <div className="flex items-center gap-2">
              <button
                onClick={openNotifPanel}
                className="relative p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
                title="Notifications"
              >
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                {notifUnreadCount > 0 && (
                  <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-[#0f2752]" />
                )}
              </button>
              <div className="relative">
                <button
                  onClick={() => setShowHamMenu(!showHamMenu)}
                  className="p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
                  title="Settings"
                >
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </button>
                {showHamMenu && (
                  <div className="absolute top-full right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-100 py-1 min-w-[140px] z-50">
                    <button
                      onClick={() => { setShowHamMenu(false); handleLogout(); }}
                      className="w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 font-medium"
                    >
                      🚪 Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Welcome heading */}
          <div className="relative z-10">
            <h1 className="text-white text-2xl sm:text-3xl font-bold leading-snug">
              Welcome to Your<br />Learning Journey, {firstName}!
            </h1>
            <p className="text-blue-300 text-sm mt-1">Keep up the great work!</p>
          </div>
        </div>

        {/* ── Cards area — overlaps hero ── */}
        <div className="relative -mt-4 px-4 space-y-4">

          {/* Upcoming Assignments */}
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="bg-[#7c3aed] px-4 py-3 flex items-center justify-between">
              <span className="text-white font-semibold text-sm">Upcoming Assignments</span>
              <button onClick={() => setTabIndex(1)} className="text-white/70 hover:text-white transition-colors" title="View all">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
            <div className="divide-y divide-gray-100">
              {upcomingHomeworks.length > 0 ? (
                upcomingHomeworks.map(hw => {
                  const daysLeft = Math.ceil((new Date(hw.dueDate) - new Date()) / (1000 * 60 * 60 * 24));
                  const dueLabel = daysLeft <= 0 ? 'Due Today' : daysLeft === 1 ? 'Due Tomorrow' : `Due in ${daysLeft} days`;
                  const urgentColor = daysLeft <= 1 ? 'text-red-500' : daysLeft <= 3 ? 'text-amber-500' : 'text-gray-400';
                  return (
                    <div
                      key={hw.id}
                      onClick={() => setTabIndex(1)}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 active:bg-gray-100 cursor-pointer transition-colors"
                    >
                      <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <span className="text-sm">📝</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{hw.title}</p>
                        {hw.subject && <p className="text-xs text-gray-400 truncate">{hw.subject}</p>}
                      </div>
                      <span className={`text-xs font-semibold flex-shrink-0 ${urgentColor}`}>{dueLabel}</span>
                    </div>
                  );
                })
              ) : (
                <div className="px-4 py-5 text-center">
                  <p className="text-2xl mb-1">🎉</p>
                  <p className="text-sm text-gray-400">No upcoming assignments!</p>
                </div>
              )}
            </div>
            {upcomingHomeworks.length > 0 && (
              <div className="px-4 py-2 bg-gray-50 border-t border-gray-100">
                <button onClick={() => setTabIndex(1)} className="text-xs text-purple-600 font-semibold hover:text-purple-800 transition-colors">
                  View all assignments →
                </button>
              </div>
            )}
          </div>

          {/* All Modules — 4 per row */}
          <div>
            <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3">Modules</h2>
            <div className="grid grid-cols-4 gap-3">
              {allModules.map(mod => (
                <button
                  key={mod.id}
                  onClick={() => handleModuleClick(mod.id)}
                  className="relative bg-white rounded-xl py-3 flex flex-col items-center gap-1 shadow-sm hover:shadow-md active:scale-95 transition-all"
                >
                  <span className="text-2xl">{mod.icon}</span>
                  <span className="text-xs font-medium text-gray-600 text-center leading-tight px-1">{mod.label}</span>
                  {mod.badge > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold border-2 border-gray-100">
                      {mod.badge > 9 ? '9+' : mod.badge}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

        </div>
      </div>
    );
  };

  // 3. Update renderContent function
  const renderContent = () => {
    switch (tabIndex) {
      case 0: return <HomePage />;
      case 1: return <AssignmentsTab />;
      case 2: return <StudentResourcesTab studentForm={student?.form} />;
      case 3: return <TimetableTab />;
      case 4: return <QuickLinksTab />;
      case 5: return <StudentWhiteboardTab form={student?.form} whiteboards={whiteboards} />;
      case 6:
        return (
          <div>
            <SchoolPostsTab 
              studentForm={student?.form}
              studentId={student?.school_id}
              authorInfo={{
                email: student?.email || localStorage.getItem('userEmail') || `${student?.school_id}@student.school.com`,
                name: student?.full_name || localStorage.getItem('userName') || student?.school_id,
                role: 'student',
                schoolId: student?.school_id
              }}
              isMobileMenuOpen={isMobileMenuOpen}
              setIsMobileMenuOpen={setIsMobileMenuOpen}
            />
          </div>
        );
      case 7:
        return (
          <div className="h-screen flex flex-col">
            <div className="flex-1 overflow-hidden">
              <div className="max-w-7xl mx-auto h-full">
                <DashboardTab />
              </div>
            </div>
          </div>
        );
      case 8:
        return <CoursesTab />;
      case 9:
        return <EducationGames 
          teacherEmail={student?.email}
          teacherName={student?.full_name}
          onBack={() => handleTabChange(0)}
        />;

      default: return <HomePage />;
    }
  };

  // ✅ Show loading while student data is being loaded
  if (!student) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl p-8 shadow-lg">
          <h2 className="text-xl font-bold text-blue-600 mb-4">🔄 Loading Dashboard...</h2>
          <p className="text-gray-600">Please wait while we load your student data.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Shared Header for all tabs - hidden on Quick Links and Home */}
      <div className={`bg-gradient-to-r from-slate-900 via-purple-900 to-slate-900 shadow-lg border-b border-purple-500/30 sticky top-0 z-50 ${tabIndex === 4 || tabIndex === 0 ? 'hidden' : ''}`}>
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {/* Hamburger Menu for SchoolPostsTab */}
              {tabIndex === 6 && (
                <button
                  onClick={() => setIsMobileMenuOpen(true)}
                  className="lg:hidden p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors flex-shrink-0"
                  title="Filters"
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              )}
              {/* Back button - visible on all screens when inside a module */}
              {tabIndex !== 0 && (
                <button
                  onClick={() => handleTabChange(0)}
                  className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors flex-shrink-0 flex items-center gap-1"
                  title="Back to Home"
                >
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  <span className="text-white text-sm font-medium hidden sm:inline">Back</span>
                </button>
              )}
              
              <div className="flex-1 min-w-0">
                <h1 className="text-base md:text-xl font-bold text-white truncate">
                  {tabIndex === 6 ? '📰 School Posts' : `${student?.full_name || 'Student'}`}
                </h1>
                <p className="text-purple-200 text-xs">Form {student?.form}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 flex-shrink-0">

              {/* Notification Icon */}
              <button
                onClick={openNotifPanel}
                className="p-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-all relative"
                title="Notifications"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                {notifUnreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 animate-pulse">
                    {notifUnreadCount > 99 ? '99+' : notifUnreadCount}
                  </span>
                )}
              </button>
              {/* Logout Button - Desktop only */}
              <button
                onClick={handleLogout}
                className="hidden md:flex px-3 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-200 border border-red-500/30 rounded-lg transition-all text-sm"
              >
                🚪 Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 🔔 Notification Panel Dropdown */}
      {showNotifPanel && (
        <div className="fixed top-16 right-4 w-80 max-h-96 bg-white rounded-xl shadow-2xl border border-gray-200 z-[60] overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b">
            <h3 className="font-semibold text-gray-800">🔔 Notifications</h3>
            <div className="flex items-center gap-2">
              {notifUnreadCount > 0 && (
                <button
                  onClick={markAllNotifsRead}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                >
                  Mark all read
                </button>
              )}
              <button onClick={() => setShowNotifPanel(false)} className="text-gray-400 hover:text-gray-600">
                ✕
              </button>
            </div>
          </div>
          <div className="overflow-y-auto max-h-80">
            {isLoadingNotifs ? (
              <div className="py-8 text-center text-gray-400">Loading...</div>
            ) : notifList.length === 0 ? (
              <div className="py-8 text-center text-gray-400">No notifications yet</div>
            ) : (
              notifList.map((n) => (
                <div
                  key={n.id}
                  onClick={() => handleNotifClick(n)}
                  className={`px-4 py-3 border-b last:border-b-0 cursor-pointer ${!n.is_read ? 'bg-blue-50' : 'bg-white'} hover:bg-gray-100 transition-colors`}
                >
                  <div className="flex items-start gap-2">
                    {!n.is_read && <div className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 flex-shrink-0"></div>}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{n.title}</p>
                      {n.message && <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.message}</p>}
                      <p className="text-xs text-gray-400 mt-1">
                        {n.created_at ? new Date(n.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                      </p>
                    </div>
                    <svg className="w-4 h-4 text-gray-400 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
      
      {/* ✅ Homework File Submission Modal */}
      {submitModalHw && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={(e) => { if (e.target === e.currentTarget) { setSubmitModalHw(null); setSubmitFile(null); } }}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-green-50 to-blue-50">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">📤 Submit Your Work</h3>
                  <p className="text-sm text-gray-600 mt-0.5">{submitModalHw.title}</p>
                </div>
                <button onClick={() => { setSubmitModalHw(null); setSubmitFile(null); }} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600">Upload a photo or file of your completed work.</p>
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-green-400 transition-colors">
                <input
                  type="file"
                  id="hw-submit-file"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.heic,.heif"
                  capture="environment"
                  onChange={(e) => setSubmitFile(e.target.files[0])}
                  className="hidden"
                />
                <label htmlFor="hw-submit-file" className="cursor-pointer">
                  <div className="text-4xl mb-2">📷</div>
                  <p className="text-sm font-medium text-gray-700">
                    {submitFile ? submitFile.name : 'Tap to take a photo or choose a file'}
                  </p>
                  {submitFile && (
                    <p className="text-xs text-green-600 mt-1">
                      {(submitFile.size / 1024).toFixed(1)} KB ready to upload
                    </p>
                  )}
                </label>
              </div>
              {submitFile && submitFile.type?.startsWith('image/') && (
                <div className="rounded-lg overflow-hidden border border-gray-200">
                  <img src={URL.createObjectURL(submitFile)} alt="Preview" className="w-full max-h-48 object-contain bg-gray-50" />
                </div>
              )}
            </div>
            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => { setSubmitModalHw(null); setSubmitFile(null); }}
                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 font-medium text-sm"
              >
                Cancel
              </button>
              <button
                disabled={!submitFile || submitting}
                onClick={async () => {
                  if (!submitFile || !student?.school_id) return;
                  setSubmitting(true);
                  try {
                    const formData = new FormData();
                    formData.append('homework_id', submitModalHw.id);
                    formData.append('file', submitFile);
                    const res = await fetch(buildApiUrl(`/submit-homework-file/${student.school_id}`), { method: 'POST', body: formData });
                    const data = await res.json();
                    if (res.ok) {
                      alert('Work submitted successfully! ✅');
                      setSubmitModalHw(null);
                      setSubmitFile(null);
                      // Update submission map locally so it shows as submitted
                      setSubmissionMap(prev => ({
                        ...prev,
                        [student.school_id]: {
                          ...(prev[student.school_id] || {}),
                          [submitModalHw.id]: { submitted_at: new Date().toISOString(), file_url: data.file_url, result: { score: 0, total: 0, answers: [] } }
                        }
                      }));
                    } else {
                      alert('Submission failed: ' + (data.detail || 'Unknown error'));
                    }
                  } catch (err) {
                    console.error('Submission error:', err);
                    alert('Error submitting work. Please try again.');
                  } finally {
                    setSubmitting(false);
                  }
                }}
                className={`flex-1 px-4 py-2.5 rounded-xl text-white font-medium text-sm transition-all ${
                  !submitFile || submitting ? 'bg-gray-300 cursor-not-allowed' : 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 shadow-lg hover:shadow-xl'
                }`}
              >
                {submitting ? '⏳ Uploading...' : '📤 Submit Work'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Content */}
      {renderContent()}
    </div>
  );
};

export default StudentDashboard;
